
// Vuejs
import { Component, Vue, Watch, Ref } from "vue-property-decorator";
import { namespace, Getter, State } from "vuex-class";
import vSelect from "vue-select";
import { Datetime } from "vue-datetime";
import moment from "moment";
import { mixins } from "vue-class-component";
import { formatDateMixin } from "@/mixins";
const { relativeDaysDateFormatted, toEndOfDay, toStartOfDay } = formatDateMixin.methods;

// Components
import DynamicDataListSelector from "@/components/DynamicDataListSelector.vue";
import EventOutcomeSelector from "@/components/EventOutcomeSelector.vue";
import ShareCustomReports from "@/components/reports/ShareCustomReports.vue";
import FavoriteReports from "@/components/reports/FavoriteReports.vue";
import HistoryModal from "@/components/reports/ReportHistoryModal.vue";
import TemplateReportBuilder from "./TemplateReportBuilder.vue";
import InsightsGraph from "./InsightsGraph.vue";
import vselect3 from "vselect3";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import { stringify } from "qs";
import { debounce, get, sortBy } from "lodash";

// Filter Components
import MultiselectFilter from "@/components/reports/filters/MultiselectFilter.vue";
import SelectFilter from "@/components/reports/filters/SelectFilter.vue";
import TextFilter from "@/components/reports/filters/TextFilter.vue";

// Types
import {
	FilterOptions,
	Filters,
	ExcludableFilters,
	EmitFilter,
	EmitExclude,
	FilterOption,
	FilterNames
} from "@/types/reports/Filters";
import {
	ReportTitles,
	ReportFilterQuery,
	ReportUpdateFilterQuery,
	FilterOptionData,
	CustomReportModal, 
	ReportTypes, 
	ShareCustomReportModal,
	FavoriteReportModal,
	ReportHistoryModal,
	ReportState,
	ReportFavoriteData
} from "@/store/reports/types";
import { FeaturesList, RootState, UserPermissions } from "@/store/types";
import { Day, DayKeyValuePair } from "@/types/Day";
import { Pref } from "@/store/settings/types";

// Utility extensions
import "@/scripts/array-operations";
import { getNamesAndValuesOfEnum } from "@/utils/enumHelper";
import { createReportUrl, fixDate } from "@/utils/reports-utils";

//CustomReportsAPI
import { AxiosResponse } from "axios";
import CustomReportsApi, { CustomReportEnabledResponse } from "@/services/api.customReports.service";

const Reports = namespace("reports");
const SettingsStore = namespace("settings");

@Component({
	components: {
		"v-select": vSelect,
		datetime: Datetime,
		"event-outcome-selector": EventOutcomeSelector,
		"template-report-builder": TemplateReportBuilder,
		"insights-graph": InsightsGraph,
		"dynamic-data-list-selector": DynamicDataListSelector,
		"vue-perfect-scrollbar": VuePerfectScrollbar,
		vselect3,
		"filter-multiselect": MultiselectFilter,
		"filter-select": SelectFilter,
		"filter-text": TextFilter,
		ShareCustomReports,
		FavoriteReports,
		"history-modal": HistoryModal
	}
})
export default class ReportBuilder extends Vue {
	@Reports.Action downloadReport: any;
	@Reports.Action fetchReportServiceEndpoint: any;
	@Reports.Action setCustomTitle: (item: ReportTitles) => Promise<void>;
	@Reports.Action getQueriedFilter: (item: ReportFilterQuery) => Promise<void>;
	@Reports.Action getAlarmPointsByAreas: (item: ReportUpdateFilterQuery) => Promise<void>;
	@Reports.Action loadDatePref: () => Promise<void>;

	@Reports.Getter("getReport") report;
	@Reports.Getter("getRelativeDaysToReport") relativeDaysToReport;
	@Reports.Getter("getCurrentReportUrl") currentReportUrl;
	@Reports.Getter("getReportLoadingStatus") loading;
	@Reports.Getter("getReportErrorStatus") error;
	@Reports.Getter("getReportApiEndpoint") reportApiEndpoint;
	@Reports.Getter("getReportTypes") reportTypes;

	@Reports.State ReportDateFormatPref: boolean;
	@Reports.State((state: ReportState) => state.FavoriteReports) private favoriteReports: ReportFavoriteData[];

	@Reports.Mutation setCustomReportModalShown: (customReportModal: CustomReportModal) => void;
	@Reports.Mutation setShareCustomReportModalShown: (shareCustomReportModal: ShareCustomReportModal) => void;
	@Reports.Mutation setFavoriteReportModalShown: (shareCustomReportModal: FavoriteReportModal) => void;
	@Reports.Mutation setReportHistoryModalShown: (reportHistoryModal: ReportHistoryModal) => void;
	@Reports.Mutation setRelativeDaysToReport: any;
	@Reports.Mutation resetState: any;
	@Getter getPermissions: UserPermissions;
	@Getter getUserId: number;
	@State((state: RootState) => state.featuresList) private featuresList: FeaturesList;

	$refs: {
		chartContainer: any;
		insights: any;
	};

	@Ref() relativeDaysSelect: vselect3;

	private reportPermalink: string = "";
	private showPeralinkCopy: boolean = false;
	private reportGeoLink: string = "";
	private showGeoLinkCopy: boolean = false;
	private enableGeoLink: boolean = false;
	private filterModal: boolean = false;
	private filterLockStatus = false;
	public chartType = "ColumnChart";
	private timeZone = "UTC";
	private errorMessage: string = "";
	private selectedUrl: string = "";
	private search: string = "";
	private filterOptions: any = FilterOptions;
	private excludableFilters: any = ExcludableFilters;
	private filterNames: any = FilterNames;
	private selectedFavoriteReport: ReportFavoriteData  = {
				insightsFavoriteID: 0,
				insightID: 0,
				queryString: "",
				userID: 0, 
				name: ""
			};

	private editReportTitle: boolean = false;
	private customReportTitle: string = "";
	private searchReport: string = "";
	readonly customReportRequestStatusSuccessful: number = 200;
	private customReportsEnabled: boolean = false;

	private readonly PrefTypeId_ESRIReportAuthToken: number = 306;

	private relativeDaysOptions = [
		{ label: "1 Day", value: "1" },
		{ label: "3 Days", value: "3" },
		{ label: "7 Days", value: "7" },
		{ label: "14 Days", value: "14" },
		{ label: "30 Days", value: "30" },
		{ label: "60 Days", value: "60" },
		{ label: "90 Days", value: "90" },
		{ label: "180 Days", value: "180" },
		{ label: "365 Days", value: "365" }
	];

	// object to hold report filtering options
	public selectedFilters: Filters = {
		groupId: [],
		responseId: [],
		eventTypeId: [],
		userId: [],
		eventOutcomeId: [],
		serverTypeEventNum: [],
		excludeAutoHandled: { id: 1, title: "Yes" },
		resultCount: { id: 100, title: "100" },
		startDate: relativeDaysDateFormatted(this.relativeDaysToReport),
		endDate: relativeDaysDateFormatted(0),
		startTime: toStartOfDay(moment().format()),
		endTime: toEndOfDay(moment().format()),
		days: [],
		excludeFilters: [],
		excludeParkedTime: { id: 1, title: "Yes" },
		ignoreDateTime: { id: 0, title: "No" }
	};

	// used to store the filtering options that have been applied to the current report to be passed to the backend when requesting an export of the report data.
	private reportOptionsPayload = {};

	private showDaysErrorMessage: boolean = false;
	private useAdvancedDateFilters: boolean = false;

	// Insights graph chart
	private get chartObject() {
		return {
			chartType: this.chartType,
			canDrilldown: true
		};
	}

	public get dateTimePickerFormat(): string {
		return this.ReportDateFormatPref ? "dd LLL yyyy HH:mm" : "dd/MM/yyyy HH:mm";
	}

	public get datePickerFormat(): string {
		return this.ReportDateFormatPref ? "dd LLL yyyy" : "dd/MM/yyyy";
	}

	// set a flag as to if we have applied any filters
	// Check if any filters have been allied to show that there is filters applied.
	private get filterApplied() {
		return (
			(this.selectedFilters.groupId && this.selectedFilters.groupId.length > 0) ||
			(this.selectedFilters.responseId && this.selectedFilters.responseId.length > 0) ||
			(this.selectedFilters.eventTypeId && this.selectedFilters.eventTypeId.length > 0) ||
			(this.selectedFilters.userId && this.selectedFilters.userId.length > 0) ||
			(this.selectedFilters.eventOutcomeId && this.selectedFilters.eventOutcomeId.length > 0) ||
			(this.selectedFilters.serverTypeEventNum && this.selectedFilters.serverTypeEventNum.length > 0) ||
			(this.selectedFilters.resultCount && this.selectedFilters.resultCount.id != 100) ||
			(this.selectedFilters.excludeAutoHandled && this.selectedFilters.excludeAutoHandled.id == 0) ||
			(this.selectedFilters.excludeFilters && this.selectedFilters.excludeFilters.length > 0) || 
			(this.selectedFilters.excludeParkedTime && this.selectedFilters.excludeParkedTime.id == 0) ||
			(this.selectedFilters.ignoreDateTime && this.selectedFilters.ignoreDateTime.id == 1) 
		);
	}

	// return if the current report has any other graph types available
	private get chartOptionsAvailable() {
		return this.report.metricColumns && this.report.metricColumns.length > 0;
	}

	private get daysOfWeek(): DayKeyValuePair[] {
		return getNamesAndValuesOfEnum<DayKeyValuePair>(Day, "title", "id");
	}

	private get advancedDateFiltersFeatureEnabled(): boolean {
		return get(this.featuresList, ["Insights", "ShowAdvancedDateFilters"], false);
	}

	private get reportFilterOptions(): any {
		if (this.advancedDateFiltersFeatureEnabled) {
			//startDate and endDate will be rendered separately if the advanced filters feature is enabled
			return this.report.filterOptions.filter(o => o.filterName !== "startDate" && o.filterName !== "endDate");
		}

		return this.report.filterOptions;
	}

	private get advancedDateTimeFilterType(): string {
		return this.useAdvancedDateFilters ? "date" : "datetime";
	}

	private get advancedDateTimeFilterPickerFormat(): string {
		return this.useAdvancedDateFilters ? this.datePickerFormat : this.dateTimePickerFormat;
	}

	private get dateTitle(): string {
		if (this.useAdvancedDateFilters) {
			const startDateTitle = this.getDateOnlyFormat(this.selectedFilters.startDate);
			const endDateTitle = this.getDateOnlyFormat(this.selectedFilters.endDate);
			const daysTitle = sortBy(this.selectedFilters.days, "id")
				.map(d => this.firstCharactersOfString(d.title, 3))
				.join(", ");
			const startTimeTitle = this.getTimeOnlyFormat(this.selectedFilters.startTime);
			const endTimeTitle = this.getTimeOnlyFormat(this.selectedFilters.endTime);

			return `${startDateTitle} - ${endDateTitle} (${daysTitle} between ${startTimeTitle} and ${endTimeTitle})`;
		}

		return `${this.formatDate(this.selectedFilters.startDate)} - ${this.formatDate(this.selectedFilters.endDate)}`;
	}

	private get filteredReportTypes(): ReportTypes[] {
		const searchText = this.searchReport;
		let searchReports = [];
	
		if (searchText !== null && searchText.trim().length > 0) {
			this.reportTypes.forEach(category => {
				let filtetedReports = category.types.filter(report => report.title.toLowerCase().includes(searchText.toLowerCase()));
				if (filtetedReports.length > 0) {
					let returnCategory: ReportTypes = {
						title: category.title,
						types: filtetedReports
					};
					searchReports.push(returnCategory)
				}
			});
			return searchReports;
		} else {
			return this.reportTypes;
		}
	}

	@Watch("report")
	// when a report is requested, update the chart type as set on the new report
	onReportChanged(newReport: any, oldReport: any) {
		this.chartType = newReport.chartType;

		// if any of the report results have a latLong property and it is populated, enable the button
		if (newReport && newReport.data && newReport.data.some(s => (s.latLong && s.latLong.length > 0) || 
																(s.latitude && s.latitude.length > 0))) {
			this.enableGeoLink = true;
		} else {
			this.enableGeoLink = false;
		}

		if (this.enableGeoLink === false && newReport.title !== undefined && newReport.title !== null) {
			if (newReport.title === "Active Alarms" || newReport.title === "Mobile Guards") {
				this.enableGeoLink = true;
			}
		}
	}

	private async created() {
		await this.fetchReportServiceEndpoint();
		await this.loadDatePref();
	}

	private async mounted() {
		this.setRelativeDaysToReport(7);

		let response: AxiosResponse<CustomReportEnabledResponse> = await CustomReportsApi.customReportsEnabled();
		if (response && response.data && response.status === this.customReportRequestStatusSuccessful) {
			this.customReportsEnabled = response.data.customReportingEnabled;
		}
	}

	async destroyed() {
		this.resetState();
	}

	private goToReport(report) : void {
		//If report is already selcted then do not run report
		//Unless the report clicked is a favorite
		if ((this.selectedUrl === report.endpoint && this.selectedFavoriteReport.insightsFavoriteID === 0 && report.favoriteID === 0 ) ||
			(this.selectedFavoriteReport.insightsFavoriteID === report.favoriteID && report.favoriteID != 0)) {
				return
		}
		this.selectedUrl = report.endpoint;
		this.filterLockStatus = false;
		this.resetFilters();

		if(report.favoriteID > 0){
			this.selectedFavoriteReport = this.favoriteReports.find(r => r.insightsFavoriteID == report.favoriteID); 

			//if report endpoint contains query strings they need to be removed first
			let url = "/reports" + 
						report.endpoint.split('?')[0] +
						"?" +
						this.favoriteReports.find(f => f.insightsFavoriteID == report.favoriteID).queryString; 

			this.$router.push(url);
		}
		else {
			this.selectedFavoriteReport = {
				insightsFavoriteID: 0,
				insightID: 0,
				queryString: "",
				userID: 0, 
				name: ""
			};
			this.$router.push(`/reports${report.endpoint}`);
		}
		
		this.chartType = this.report.chartType;
	}

	private updateChartType(type) {
		this.chartType = type;
	}

	private setSelectedRelativeDays(value) {
		this.setRelativeDaysToReport(+value["value"]);
	}

	private setCustomRelativeDays(days: number) {
		if (days <= 0 || days > 365) return; // Failed validation
		this.relativeDaysSelect.onAfterSelect();
		this.setRelativeDaysToReport(+days);
	}

	private async downloadReportCsv() {
		await this.downloadReport(this.reportOptionsPayload);
	}

	private formatDate(dateStr) {
		dateStr = fixDate(dateStr);
		let date = new Date(dateStr);

		let monthNames = ["Jan", "Feb", "March", "April", "May", "June", "July", "Aug", "Sept", "Oct", "Nov", "Dec"];

		let day = date.getDate();
		let monthIndex = date.getMonth();
		let year = date.getFullYear();
		let hours = date.getHours();
		let mins = date.getMinutes();

		return day + " " + monthNames[monthIndex] + " " + year + " " + hours + ":" + (mins < 10 ? "0" : "") + mins;
	}

	private outcomeChanged(value) {
		if (value) {
			this.selectedFilters.eventOutcomeId.splice(0, 9, { id: value });
		} else {
			this.selectedFilters.eventOutcomeId = [];
		}
	}

	private ddlChanged(value: any) {
		this.selectedFilters.dynamicdata = value;

		if (value.length == 0) {
			delete this.selectedFilters.dynamicdata;
		}
	}

	private callReport() {
		if (this.useAdvancedDateFilters && this.selectedFilters.days.length === 0) {
			this.showDaysErrorMessage = true;
			return;
		}

		if (!this.useAdvancedDateFilters) {
			this.selectedFilters.days = [];
		}

		const filters = Object.assign({}, this.selectedFilters);

		filters.startDate = fixDate(this.selectedFilters.startDate);
		filters.endDate = fixDate(this.selectedFilters.endDate);

		this.$refs.insights.runV2Report(this.currentReportUrl, true, filters);
		this.filterModal = false;
	}

	private get getErrorMessage() {
		return this.errorMessage !== ""
			? this.errorMessage
			: "An error has occurred retrieving the requested report, please try again";
	}

	private resetFilters() {
		this.selectedFilters = {
			groupId: [],
			responseId: [],
			eventTypeId: [],
			userId: [],
			eventOutcomeId: [],
			serverTypeEventNum: [],
			excludeAutoHandled: { id: 1, title: "Yes" },
			resultCount: { id: 100, title: "100" },
			startDate: relativeDaysDateFormatted(this.relativeDaysToReport),
			endDate: relativeDaysDateFormatted(0),
			startTime: toStartOfDay(moment().format()),
			endTime: toEndOfDay(moment().format()),
			days: [],
			excludeFilters: [], 
			excludeParkedTime: { id: 1, title: "Yes" },
			ignoreDateTime: { id: 0, title: "No" }
		};

		this.useAdvancedDateFilters = false;
	}

	private createGeoUrl(reportUrl: string, urlFiltered: any) {
		const originalReportUrl = reportUrl;
		const params = [];

		for (const [key, value] of Object.entries(urlFiltered)) {
			// if the type is an object, then it is an array of ids
			if (typeof value === "object") {
				const propertyValues = Object.values(value);
				// joing them in to a single parameter eg id=1,2,3,4,5
				params.push(`${key}=${propertyValues.map(m => m.id).join(",")}`);
			} else {
				params.push(`${key}=${value}`);
			}
		}

		params.push("populatedResultsOnly=true");
		params.push(`timeZone=${this.timeZone}`);

		reportUrl = `${reportUrl}&${params.join("&")}`;

		// Check report type
		if (originalReportUrl === "/ActiveAlarmsReport" || originalReportUrl === "/MobileGuardReport") {
			if (originalReportUrl === "/ActiveAlarmsReport") {
				reportUrl = `/ActiveAlarmsReport/AuthToken`
			} else if (originalReportUrl === "/MobileGuardReport" ) {
				reportUrl = `/MobileGuardReport/AuthToken`
			}

			this.reportGeoLink = this.reportApiEndpoint.endsWith("/")
				? `${this.reportApiEndpoint}Reports/GeoJson${reportUrl}`
				: `${this.reportApiEndpoint}/Reports/GeoJson${reportUrl}`;
		}
		else {
			// http://local.sureviewsystems.com:60000/Reports/GetGeoJSON/AlarmCountReport?eventTypeId=1&startDate=2020-07-20T12%3A40%3A58.000&endDate=2020-07-27T12%3A40%3A58.000&resultCount=100&timeZone=UTC"
			// set the permalink variable so this report url can be copied by the user

			this.reportGeoLink = this.reportApiEndpoint.endsWith("/")
				? `${this.reportApiEndpoint}Reports/GetGeoJSON${reportUrl}`
				: `${this.reportApiEndpoint}/Reports/GetGeoJSON${reportUrl}`;
		}
	}

	private updatePermaLinkUrl(paramLinkParams: any) {	
		// set the permalink variable so this report url can be copied by the user
		this.reportPermalink = createReportUrl(paramLinkParams.reportUrl, paramLinkParams.urlFiltered, this.relativeDaysToReport, this.timeZone);
		this.createGeoUrl(paramLinkParams.reportUrl, paramLinkParams.urlFiltered);
	}

	private editCurrentReportTitle() {
		if (this.editReportTitle) {
			this.editReportTitle = false;
			return;
		}

		this.customReportTitle = this.report.customTitle;
		this.editReportTitle = true;
	}

	private async saveReportTitle() {
		const payload: ReportTitles = {
			title: this.report.title,
			customTitle: this.customReportTitle
		};
		await this.setCustomTitle(payload);
		this.editReportTitle = false;
	}

	private async showAndCopyReportLink() {
		// @TODO: REFACTOR - REMOVE DOM MANIPULATION
		const input = document.getElementById("link-copy-input") as HTMLInputElement;
		if (!input) return;
		this.showPeralinkCopy = true;
		await input.setAttribute("value", this.reportPermalink);
		input.select();
		document.execCommand("copy");
	}

	private async showAndCopyGeoLink() {
		const input = document.getElementById("geo-copy-input") as HTMLInputElement;
		if (!input) return;
		this.showGeoLinkCopy = true;
		await input.setAttribute("value", this.reportGeoLink);
		input.select();
		document.execCommand("copy");
	}

	/**
	 * Watch the groups selected as when the selected groups are updated,
	 * so are the alarm points in the filter menu.
	 * @param newValue the new area selected that we want to filter alarm points on.
	 */
	@Watch("selectedFilters.groupId", { deep: true })
	public async alarmPointsByArea(newValue: FilterOptionData[]) {
		// Ensure logic is not run when new value is falsy.
		if (!newValue) {
			return;
		}
		const payload: ReportUpdateFilterQuery = {
			endpoint: "QueryAlarmPointsByArea",
			query: {
				queries: newValue
			},
			filterName: "responseId"
		};
		this.getAlarmPointsByAreas(payload);
	}

	/**
	 * @param query  the string that was inputted by the user that we want to query on
	 * @param filterName filter name used to get the endpoint, this is unique to each filter.
	 * @param loading callback method to enable / disable the spinner icon
	 */
	private async fetchQueriedFilter(query: string, filterName: string, loading: any): Promise<void> {
		const endpoint: string = this.report.filterEndpoints[filterName];
		// This filter type didn't have a endpoint assigned to it.
		if (!endpoint) return;
		loading(true);

		if (!this.canFilterInternally(filterName) || query.length == 0 || this.search.includes(query)) {
			this.search = query;
			const payload: ReportFilterQuery = {
				endpoint: endpoint,
				query: query,
				filterName: filterName
			};
			await this.getQueriedFilter(payload);
		}
		this.search = query;

		loading(false);
	}

	/**
	 * debounce method to handle our waiting time when user is inputting data
	 * @param query Data that the user has inserted
	 * @param loading callback method to enable / disable loading icon
	 * @param filterName filter name to find the endpoint, this is unique.
	 */
	private debounceFilterQuery = debounce(
		async (query: string, loading: any, filterName: string) =>
			await this.fetchQueriedFilter(query, filterName, loading),
		500
	);

	private canFilterInternally(filterName: string): boolean {
		if (!this.report || !this.report.filterEndpoints || !this.report.filterEndpoints.hasOwnProperty(filterName))
			return true;
		const endpoint: string = this.report.filterEndpoints[filterName];
		const filter = this.report.filterOptions.find(filter => filter.filterName == filterName);

		if (!filter) return true;

		if (!endpoint) return true;

		if (filter && filter.data && filter.data.length < 180) return true;

		return false;
	}

	public getOptionKey(option: any) {
		return JSON.stringify(option).replace(/ /g, "");
	}

	private sanitizeToNumbers(event) {
		const keyCode = event.keyCode ? event.keyCode : event.which;
		if (keyCode < 48 || keyCode > 57) {
			event.preventDefault();
		}
	}

	private firstCharactersOfString(string: string, characters: number): string {
		return string.substring(0, characters);
	}

	private dayIsIncludedInFilter(day: DayKeyValuePair): boolean {
		return this.getDayItemIndex(day) !== -1;
	}

	private getDayItemIndex(day): number {
		//queryString will return the days selected as strings, so convert the number to a string to cover both cases
		return this.selectedFilters.days.findIndex(d => d.id.toString() === day.id.toString());
	}

	private toggleDayIncludedInFilter(day): void {
		const itemIndex = this.getDayItemIndex(day);

		if (itemIndex !== -1) {
			this.selectedFilters.days.splice(itemIndex, 1);
		} else {
			this.selectedFilters.days.push(day);
		}

		this.toggleDaysErrorMessage();
	}

	private toggleDaysErrorMessage(): void {
		this.showDaysErrorMessage = this.selectedFilters.days.length === 0;
	}

	private updateReportFilters(filters: any): void {
		this.selectedFilters = filters;

		if (this.selectedFilters.days.length) {
			this.useAdvancedDateFilters = true;
		}
	}

	private getDateOnlyFormat(date: string): string {
		return new Date(date)
			.toLocaleString("default", { day: "numeric", year: "numeric", month: "short" })
			.replace(",", "");
	}

	private getTimeOnlyFormat(date: string): string {
		return moment(date)
			.local()
			.format("HH:mm");
	}

	private toggleExcludeState(filterExclude: EmitExclude) {
		const index = this.selectedFilters.excludeFilters.findIndex(
			x => x.filterName == filterExclude.filterName && x.filterType == filterExclude.filterType
		);

		// Add to excluded filter
		if (filterExclude.state && index == -1) {
			this.selectedFilters.excludeFilters.push({
				filterType: filterExclude.filterType,
				filterName: filterExclude.filterName,
				value: filterExclude.state
			});
		}

		if (!filterExclude.state && index >= 0) {
			this.selectedFilters.excludeFilters.splice(index, 1);
		}
	}

	private setTextFilter(filter: EmitFilter): void {
		if (!filter.filter && this.selectedFilters.hasOwnProperty(filter.filterName)) {
			delete this.selectedFilters[filter.filterName];
			return;
		}
		this.selectedFilters[filter.filterName] = filter.filter;
	}

	private setCustomReportModalOpen(selectedID: number, isShared: boolean): void {
		this.setCustomReportModalShown({ shown: true, insightID: selectedID, isShared: isShared});
	}

	private showEditCustomReportButton(report: any): boolean {
		if (this.customReportsEnabled && report.customScript !== null && report.createdBy === this.getUserId) {
			return true;
		} else {
			return false;
		}
	}

	private setShareCustomReportModalOpen(selectedID: number, reportNameSelected: string): void {
		this.setShareCustomReportModalShown({shown: true, insightID: selectedID, reportName: reportNameSelected});		
	}

	private reportShareEnabled(report: any): boolean {
		if (this.getPermissions.canShareReports && report.customScript !== null && report.createdBy === this.getUserId)
		{
			return true;
		} else{
			return false;
		}
	}

	private favouriteModal(): void{
		this.setFavoriteReportModalShown(
		{
			favoriteID: this.selectedFavoriteReport.insightsFavoriteID, 
			shown: true, 
			currentUrl: this.currentReportUrl, 
			reportPermalink: 
			this.reportPermalink
		});
	}

	private favoriteAdded(favoriteID: number): void {
		this.selectedFavoriteReport = this.favoriteReports.find(r => r.insightsFavoriteID == favoriteID);
	}

	private removeSelectedFavorite(favoriteID: number) : void {
		if(favoriteID == this.selectedFavoriteReport.insightsFavoriteID){
			this.selectedFavoriteReport = {
				insightsFavoriteID: 0,
				insightID: 0,
				queryString: "",
				userID: 0, 
				name: ""
			};
		}
	}

	private showHistoryModal(): void {
		this.setReportHistoryModalShown({shown: true})		
	}

	private getReportDisplayName(report) : string{
		let reportName : string = "";

		if(report.customTitle){
			reportName = report.customTitle;
		}
		else if(report.favoriteName) {
			reportName = report.favoriteName;
		}
		else {
			reportName = report.title;
		}
		
		if(reportName.length > 36){
			reportName = reportName.substring(0, 33).trim() + "...";
		}

		return reportName;
	}

	private highlightSelected(report) : boolean{
		if(report.endpoint === this.currentReportUrl && this.selectedFavoriteReport.insightsFavoriteID === 0 && report.favoriteID === 0 || 
			report.favoriteID === this.selectedFavoriteReport.insightsFavoriteID && this.selectedFavoriteReport.insightsFavoriteID != 0
		)
		{
			return true;
		}
		else {
			return false;
		}
	} 

	//checks if current report url is a custom report for visual changes
	private get isReportCustom(): boolean {
		const url: string = this.currentReportUrl;

		if (!url || !this.reportTypes) {
			return false;
		}

		// Iterate through report categories and types, return true if found
		for (var i = 0; i < this.reportTypes.length; i++) {
			for (var y = 0; y < this.reportTypes[i].types.length; y++) {
				if (url.includes(this.reportTypes[i].types[y].endpoint)) {
					if(this.reportTypes[i].types[y].customScript != null){
						return true;
					} else {
						return false;
					}
				}
			}
		}

		return false;
	}

	private createCategoryKey(title: string) : string {
		return title.toLowerCase().replace(/\s/g, '');
	}
}
